// Facebook Post Publishing - Fixed endpoint and field names
import React, { useState, useEffect, useCallback, useRef } from "react";
import Sidebar, { SidebarProvider } from "../../components/navigation/Sidebar";
import { supabase } from "../../lib/supabase";
import { useModal } from "../../components/ui/ModalProvider";
import { useLanguageProfile } from "../../contexts/LanguageProfileContext";
import { useRequestGuard } from "../../hooks/useRequestGuard";
import {
  fetchFacebookPosts,
  createFacebookPost,
  deleteFacebookPost,
  deleteFacebookPosts,
  fetchFacebookPages,
  uploadPostImages,
  getFacebookPagePictureUrl,
  createBulkScheduleBatch,
  resumeBulkScheduleBatch,
  fetchLatestBulkBatch,
  fetchBulkBatchFailure,
  acknowledgeBulkBatch
} from "../../services/facebookPostsService";
import { generateHumanSchedule, toDatetimeLocalValue } from "../../utils/humanSchedule";
import {
  Plus,
  Search,
  RefreshCw,
  Calendar,
  Link as LinkIcon,
  MessageCircle,
  Image as ImageIcon,
  X,
  Smile,
  Hash,
  Clock,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Trash2,
  Eye,
  Edit,
  Globe,
  FileText,
  Layers,
  Zap,
  AlertCircle
} from "lucide-react";
import { wordpressService } from "../../services/wordpressService";
import { fetchArticlesFast } from "../../services/articleScraperService";

// Recipe JSON-LD extraction helpers (used for Full Recipe post mode)
function extractRecipeJsonLd(html) {
  const regex = /<script[^>]+type=["']application\/ld\+json["'][^>]*>([\s\S]*?)<\/script>/gi;
  let m;
  while ((m = regex.exec(html)) !== null) {
    try {
      const data = JSON.parse(m[1]);
      const items = Array.isArray(data) ? data : [data];
      for (const item of items) {
        if (item?.['@type'] === 'Recipe') return item;
        if (Array.isArray(item?.['@graph'])) {
          const r = item['@graph'].find(g => g?.['@type'] === 'Recipe');
          if (r) return r;
        }
      }
    } catch {}
  }
  return null;
}

function formatRecipePost(title, recipe) {
  const ingredients = recipe?.recipeIngredient || [];
  const instructions = recipe?.recipeInstructions || [];
  let content = title;
  if (ingredients.length) {
    content += '\n\nIngredients:\n\n' + ingredients.map(i => `• ${i}`).join('\n');
  }
  if (instructions.length) {
    const steps = instructions
      .map((step, i) => {
        const text = typeof step === 'string' ? step : (step.text || step.description || '');
        return text ? `${i + 1}. ${text}` : null;
      })
      .filter(Boolean);
    if (steps.length) content += '\n\nDirections:\n\n' + steps.join('\n');
  }
  return content;
}


export default function PostsPlanner() {
  const { alert, confirm } = useModal();
  const { activeProfile } = useLanguageProfile();
  const startPostsRequest = useRequestGuard();
  const startPagesRequest = useRequestGuard();
  const startWebsitesRequest = useRequestGuard();
  const startArticlesRequest = useRequestGuard();
  const startBulkBatchRequest = useRequestGuard();
  const functionsUrl = import.meta.env.VITE_SUPABASE_FUNCTIONS_URL;
  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  // State
  const [posts, setPosts] = useState([]);
  const [pages, setPages] = useState([]);
  const [pageImgErrors, setPageImgErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [postsLoading, setPostsLoading] = useState(false);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState("all");
  const [pageFilter, setPageFilter] = useState("");
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const [searchQuery, setSearchQuery] = useState("");
  // Per-status counts for the filter pills — reflects the current page/date
  // scope regardless of which status is selected, so pill counts stay
  // accurate even while a single status is active.
  const [statusCounts, setStatusCounts] = useState({ all: 0, scheduled: 0, published: 0, draft: 0, failed: 0 });
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  
  // Form state
  const [postContent, setPostContent] = useState("");
  const [firstComment, setFirstComment] = useState("");
  const [selectedImages, setSelectedImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [postNow, setPostNow] = useState(true);
  const [scheduledTime, setScheduledTime] = useState("");
  const [selectedPages, setSelectedPages] = useState([]);
  const [scheduleError, setScheduleError] = useState("");
  
  // Table pagination
  const TABLE_PAGE_SIZE = 40;
  const [tablePage, setTablePage] = useState(1);

  // Bulk selection (Posts Planner table) — keyed by real post.id, scoped to
  // the currently visible page so Select All never reaches into other pages
  // or filtered-out posts.
  const [selectedPostIds, setSelectedPostIds] = useState(new Set());
  const selectAllRef = useRef(null);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Search is applied client-side over the already-loaded posts (mirrors
  // the other client-side filtering already done in this component).
  const searchedPosts = searchQuery.trim()
    ? posts.filter((p) => {
        const q = searchQuery.trim().toLowerCase();
        return (p.message || p.content || "").toLowerCase().includes(q)
          || (p.first_comment || "").toLowerCase().includes(q);
      })
    : posts;

  // Derived table page slice — shared by the table render below and by the
  // selection logic (Select All / header checkbox) so both always agree on
  // exactly which rows are "currently visible".
  const totalTablePages = Math.max(1, Math.ceil(searchedPosts.length / TABLE_PAGE_SIZE));
  const safeTablePage = Math.min(tablePage, totalTablePages);
  const pagedPosts = searchedPosts.slice((safeTablePage - 1) * TABLE_PAGE_SIZE, safeTablePage * TABLE_PAGE_SIZE);
  const selectedOnPageCount = pagedPosts.filter((p) => selectedPostIds.has(p.id)).length;
  const isAllOnPageSelected = pagedPosts.length > 0 && selectedOnPageCount === pagedPosts.length;

  useEffect(() => {
    if (!selectAllRef.current) return;
    selectAllRef.current.indeterminate = selectedOnPageCount > 0 && selectedOnPageCount < pagedPosts.length;
  }, [selectedOnPageCount, pagedPosts.length]);

  // Drop any selected ids that are no longer part of the loaded posts (e.g.
  // a filter change, a refresh, or the post itself being deleted elsewhere)
  // so selection never silently references stale/hidden posts.
  useEffect(() => {
    setSelectedPostIds((prev) => {
      if (prev.size === 0) return prev;
      const validIds = new Set(posts.map((p) => p.id));
      const next = new Set([...prev].filter((id) => validIds.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [posts]);

  // Changing table page changes which rows are "visible" — selection is
  // page-scoped, so clear it on page change rather than letting it silently
  // carry over to a different set of rows.
  useEffect(() => {
    setSelectedPostIds(new Set());
  }, [tablePage]);

  const toggleSelectPost = (postId) => {
    setSelectedPostIds((prev) => {
      const next = new Set(prev);
      if (next.has(postId)) next.delete(postId);
      else next.add(postId);
      return next;
    });
  };

  const toggleSelectAllOnPage = () => {
    setSelectedPostIds((prev) => {
      const next = new Set(prev);
      pagedPosts.forEach((p) => {
        if (isAllOnPageSelected) next.delete(p.id);
        else next.add(p.id);
      });
      return next;
    });
  };

  // Article importer state
  const [selectedWebsiteId, setSelectedWebsiteId] = useState("");
  const [selectedArticleId, setSelectedArticleId] = useState("");
  const [websites, setWebsites] = useState([]);
  const [websiteArticles, setWebsiteArticles] = useState([]);
  const [articlesLoading, setArticlesLoading] = useState(false);
  const [articlesError, setArticlesError] = useState("");
  const [prefillImageUrls, setPrefillImageUrls] = useState([]);
  const [articleOffset, setArticleOffset] = useState(0);
  const [hasMoreArticles, setHasMoreArticles] = useState(false);

  // Notification
  const [notification, setNotification] = useState(null);

  // Bulk scheduler state
  const [bulkQueue, setBulkQueue] = useState([]);
  const [publishStartDate, setPublishStartDate] = useState("");
  const [bulkInterval, setBulkInterval] = useState(110);
  const [fbPostsPerDay, setFbPostsPerDay] = useState(0);
  const [bulkPageIds, setBulkPageIds] = useState([]);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkPublishing, setBulkPublishing] = useState(false);
  const bulkPublishingRef = useRef(false);

  // Scheduling progress bar — backed by fb_bulk_schedule_batches, not local
  // loop state, so it survives a refresh/navigation/browser close and
  // resumes showing the real persisted state of the background job.
  const [schedulingProgress, setSchedulingProgress] = useState({
    active: false, batchId: null, done: 0, total: 0, failed: 0, status: null,
  });
  const resumedBulkBatchIdsRef = useRef(new Set());
  const ACTIVE_PROGRESS_MAX_AGE_MS = 24 * 60 * 60 * 1000;

  // Post mode: 'withLink' (default, current behavior) | 'fullRecipe' (no link, full recipe content)
  const [bulkPostMode, setBulkPostMode] = useState('withLink');

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [editContent, setEditContent] = useState("");
  const [editFirstComment, setEditFirstComment] = useState("");
  const [editScheduledAt, setEditScheduledAt] = useState("");
  const [editSaving, setEditSaving] = useState(false);

  // Clear the previous profile's posts/pages immediately on switch so stale
  // content can't flash while the new profile's data is loading. Also drop
  // the cached article-importer website list so the modal re-fetches it
  // scoped to the newly active profile instead of reusing another profile's.
  useEffect(() => {
    startArticlesRequest();
    setPosts([]);
    setPages([]);
    setWebsites([]);
    setSelectedWebsiteId("");
    setSelectedArticleId("");
    setWebsiteArticles([]);
    setPrefillImageUrls([]);
    setArticlesError("");
    setArticlesLoading(false);
    setArticleOffset(1);
    setHasMoreArticles(false);
  }, [activeProfile?.id, startArticlesRequest]);

  // Load initial data
  useEffect(() => {
    loadData();
  }, [activeProfile?.id]);

  // Resume the persistent scheduling-progress bar from the backend on
  // mount / profile switch, and keep polling while a batch is still in
  // flight. This is what makes the progress bar survive a refresh,
  // navigating away and back, or the browser having been closed entirely —
  // it always reflects fb_bulk_schedule_batches, never locally-driven loop
  // state.
  useEffect(() => {
    let intervalId;

    const refreshBatch = async () => {
      const isCurrent = startBulkBatchRequest();
      const batch = await fetchLatestBulkBatch(activeProfile?.id || null);
      if (!isCurrent()) return;

      if (!batch || batch.acknowledged_at) {
        setSchedulingProgress({ active: false, batchId: null, done: 0, total: 0, failed: 0, status: null });
        return;
      }

      const isFinished = batch.status === "completed" || batch.status === "completed_with_errors";
      const isCancelled = batch.status === "cancelled" || batch.status === "canceled";
      const isStale = !isFinished && Date.now() - new Date(batch.created_at).getTime() > ACTIVE_PROGRESS_MAX_AGE_MS;
      setSchedulingProgress({
        active: !isFinished && !isCancelled && !isStale,
        batchId: batch.id,
        done: batch.succeeded_count + batch.failed_count,
        total: batch.total_count,
        failed: batch.failed_count,
        status: batch.status,
      });

      // A previous producer invocation may have persisted the whole batch but
      // stopped dispatching at Supabase's 1,000-row response limit. Resume an
      // active batch once per page session; the server re-checks ownership and
      // sends only untouched pending rows, so this cannot duplicate completed
      // work. A failed request is eligible for the next polling pass.
      if (!isFinished && !isCancelled && !resumedBulkBatchIdsRef.current.has(batch.id)) {
        resumedBulkBatchIdsRef.current.add(batch.id);
        resumeBulkScheduleBatch(batch.id).catch((error) => {
          resumedBulkBatchIdsRef.current.delete(batch.id);
          console.error("[FacebookPlanner] bulk schedule resume failed:", error);
        });
      }

      if ((isFinished || isCancelled) && intervalId) clearInterval(intervalId);
      if (isFinished) {
        if (batch.status === "completed_with_errors") {
          const failure = await fetchBulkBatchFailure(batch.id);
          const message = /permission|impersonating a user's page|code:\s*190/i.test(failure || "")
            ? "Facebook permissions are missing for this page. Reconnect it in Facebook Page Stats, grant all requested permissions, then schedule the post again."
            : failure || `${batch.failed_count} post${batch.failed_count !== 1 ? "s" : ""} could not be scheduled.`;
          setNotification({
            type: "error",
            batchId: batch.id,
            message,
          });
          setTimeout(() => setNotification((current) => current?.batchId === batch.id ? null : current), 8000);
        }
        await loadPosts();
      }
    };

    refreshBatch();
    intervalId = setInterval(refreshBatch, 5000);

    return () => clearInterval(intervalId);
  }, [activeProfile?.id, startBulkBatchRequest]);

  const handleDismissSchedulingProgress = async () => {
    const batchId = schedulingProgress.batchId;
    setSchedulingProgress({ active: false, batchId: null, done: 0, total: 0, failed: 0, status: null });
    if (batchId) await acknowledgeBulkBatch(batchId);
  };

  // Load WordPress sites when modal opens
  useEffect(() => {
    if (showModal && websites.length === 0) {
      const isCurrent = startWebsitesRequest();
      wordpressService.getAllSites(activeProfile?.id)
        .then((sites) => { if (isCurrent()) setWebsites(sites || []); })
        .catch((err) => { if (isCurrent()) console.error("Error loading websites:", err); });
    }
  }, [showModal, activeProfile?.id, startWebsitesRequest]);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([loadPosts(), loadPages()]);
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadPosts = async () => {
    const isCurrent = startPostsRequest();
    setPostsLoading(true);
    try {
      const filters = {
        status: statusFilter,
        pageId: pageFilter,
        startDate: dateRange.start,
        endDate: dateRange.end,
        profileId: activeProfile?.id || null,
      };
      const data = await fetchFacebookPosts(filters);
      if (!isCurrent()) return;
      setPosts(data);

      // Status pill counts must reflect every status for the current
      // page/date scope, not just the currently selected one, so fetch the
      // unfiltered-by-status set too (reuses the same existing helper —
      // no new Supabase query).
      const allStatusPosts = statusFilter === "all" ? data : await fetchFacebookPosts({ ...filters, status: "all" });
      if (!isCurrent()) return;
      const counts = { all: allStatusPosts.length, scheduled: 0, published: 0, draft: 0, failed: 0 };
      allStatusPosts.forEach((p) => {
        if (counts[p.status] !== undefined) counts[p.status] += 1;
      });
      setStatusCounts(counts);
    } catch (error) {
      if (!isCurrent()) return;
      console.error("Error loading posts:", error);
      // Use mock data for demo
      setPosts([]);
    } finally {
      if (isCurrent()) setPostsLoading(false);
    }
  };

  const loadPages = async () => {
    const isCurrent = startPagesRequest();
    try {
      const data = await fetchFacebookPages(activeProfile?.id);
      if (!isCurrent()) return;
      setPages(data || []);
    } catch (error) {
      if (!isCurrent()) return;
      console.error("Error loading pages:", error);
      setPages([]);
    }
  };

  const handleResetFilters = () => {
    setStatusFilter("all");
    setPageFilter("");
    setDateRange({ start: "", end: "" });
    setSearchQuery("");
    loadPosts();
  };

  const handleFilterChange = () => {
    loadPosts();
  };

  // Image handling
  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const newPreviews = files.map((file) => URL.createObjectURL(file));
    setSelectedImages((prev) => [...prev, ...files]);
    setImagePreviews((prev) => [...prev, ...newPreviews]);
  };

  const handleRemoveImage = (index) => {
    setSelectedImages((prev) => prev.filter((_, i) => i !== index));
    setImagePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  // Page selection
  const togglePageSelection = (pageId) => {
    setSelectedPages((prev) => 
      prev.includes(pageId) 
        ? prev.filter((id) => id !== pageId)
        : [...prev, pageId]
    );
  };

  // Article importer handlers
  const PAGE_SIZE = 20;

  const loadArticles = async (siteId, page, append) => {
    const isCurrent = startArticlesRequest();
    const site = websites.find((item) => String(item.id) === String(siteId));

    if (!site || (activeProfile?.id && String(site.profileId) !== String(activeProfile.id))) {
      setWebsiteArticles([]);
      setArticlesError("The selected website is not available in the active profile.");
      setArticlesLoading(false);
      return;
    }
    if (!site.siteUrl) {
      setWebsiteArticles([]);
      setArticlesError("The selected website does not have a WordPress URL.");
      setArticlesLoading(false);
      return;
    }

    setArticlesError("");
    setArticlesLoading(true);
    try {
      const fetched = await fetchArticlesFast(site.siteUrl, PAGE_SIZE, page, { throwOnFailure: true });
      if (!isCurrent()) return;

      const items = fetched.map((article) => ({
        ...article,
        id: null,
        language: activeProfile?.language_code || "en",
      }));

      setHasMoreArticles(items.length === PAGE_SIZE);
      setArticleOffset(page + 1);

      if (append) {
        setWebsiteArticles((prev) => {
          const existing = new Set(prev.map((article) => article.articleUrl));
          return [...prev, ...items.filter((article) => !existing.has(article.articleUrl))];
        });
      } else {
        setWebsiteArticles(items);
      }
    } catch (err) {
      if (!isCurrent()) return;
      console.error("Error loading articles:", err);
      setArticlesError(err?.message || "Unable to fetch published WordPress articles.");
      if (!append) setWebsiteArticles([]);
    } finally {
      if (isCurrent()) setArticlesLoading(false);
    }
  };

  const handleWebsiteChange = async (siteId) => {
    setSelectedWebsiteId(siteId);
    setSelectedArticleId("");
    setWebsiteArticles([]);
    setPrefillImageUrls([]);
    setArticlesError("");
    setArticleOffset(1);
    setHasMoreArticles(false);
    if (!siteId) {
      startArticlesRequest();
      setArticlesLoading(false);
      return;
    }
    await loadArticles(siteId, 1, false);
  };

  const handleShowMoreArticles = () => {
    loadArticles(selectedWebsiteId, articleOffset, true);
  };

  const handleArticleSelect = async (articleUrl) => {
    setSelectedArticleId(articleUrl);
    if (!articleUrl) return;

    const article = websiteArticles.find((a) => a.articleUrl === articleUrl);
    if (!article) return;

    // Prefill first comment with the published URL
    if (article.articleUrl) {
      setFirstComment(article.articleUrl);
    }

    // Use featured image from scraped article
    setPrefillImageUrls([]);
    if (article.featuredImageUrl) {
      setPrefillImageUrls([article.featuredImageUrl]);
    }

    setPostContent(article.recipeCardDescription || "");
  };

  const handleRemovePrefillImage = (index) => {
    setPrefillImageUrls((prev) => prev.filter((_, i) => i !== index));
  };

  // Validate form
  const validateForm = () => {
    if (!postContent.trim()) {
      alert("Please enter post content");
      return false;
    }
    if (selectedPages.length === 0) {
      alert("Please select at least one page");
      return false;
    }
    if (!postNow && !scheduledTime) {
      setScheduleError("Please select a scheduled time");
      return false;
    }
    if (!postNow && scheduledTime) {
      const schedDate = new Date(scheduledTime);
      if (schedDate <= new Date()) {
        setScheduleError("Scheduled time must be in the future");
        return false;
      }
    }
    return true;
  };

  // Create post
  const handleCreatePost = async () => {
    if (!validateForm()) return;

    setModalLoading(true);
    setScheduleError("");

    try {
      // Upload images if any; prefillImageUrls are already hosted — include directly
      let imageUrls = [...prefillImageUrls];
      if (selectedImages.length > 0) {
        const uploadedUrls = await uploadPostImages(selectedImages);
        imageUrls = [...imageUrls, ...uploadedUrls];
      }

      // Prepare post data
      const postData = {
        content: postContent,
        first_comment: firstComment,
        image_urls: imageUrls,
        page_ids: selectedPages,
        scheduled_at: postNow ? null : scheduledTime,
        post_now: postNow,           article_link: selectedArticleId ? websiteArticles.find(a => String(a.id) === String(selectedArticleId))?.wp_post_url || "" : "",
      };

      // Call API
      if (!functionsUrl) {
        throw new Error("Facebook automation service is not configured.");
      }

      const token = (await supabase.auth.getSession())?.data?.session?.access_token;
      if (!token) {
        throw new Error("Authentication expired. Please re-login.");
      }

      const res = await fetch(`${functionsUrl}/facebook-post`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          apikey: supabaseAnonKey,
        },
        body: JSON.stringify(postData),
      });

      const resData = await res.json();

      if (!res.ok) {
        // Surface the per-page error detail when available
        const firstFailure = resData?.results?.find((r) => !r.success);
        throw new Error(firstFailure?.error || resData?.error || resData?.message || "Failed to create post on server");
      }

      // Check if at least one page succeeded
      if (!resData.success) {
        const firstFailure = resData?.results?.find((r) => !r.success);
        throw new Error(firstFailure?.error || resData?.message || "Post could not be published to any page");
      }

      // Show success notification
      setNotification({
        type: "success",
        message: resData.message || "Post created successfully!"
      });

      // Close modal and reset form
      closeModal();

      // Reload posts from DB so the list reflects what was actually saved
      await loadPosts();

      // Clear notification after 3 seconds
      setTimeout(() => setNotification(null), 3000);
      
    } catch (error) {
      console.error("Error creating post:", error);
      alert(`Failed to create post: ${error.message}`);
    } finally {
      setModalLoading(false);
    }
  };

  // Delete post
  const handleDeletePost = async (postId) => {
    const confirmed = await alert("Are you sure you want to delete this post?", {
      title: "Delete Post",
      confirmText: "Delete",
      cancelText: "Cancel",
    });

    if (!confirmed) return;

    try {
      await deleteFacebookPost(postId);
      setPosts((prev) => prev.filter((p) => p.id !== postId));
      setNotification({
        type: "success",
        message: "Post deleted successfully!"
      });
      setTimeout(() => setNotification(null), 3000);
    } catch (error) {
      console.error("Error deleting post:", error);
      // Remove locally for demo
      setPosts((prev) => prev.filter((p) => p.id !== postId));
    }
  };

  // Bulk delete selected posts
  const handleBulkDeleteSelected = async () => {
    const idsToDelete = Array.from(selectedPostIds);
    if (idsToDelete.length === 0) return;

    const confirmed = await confirm(
      `Delete ${idsToDelete.length} selected post${idsToDelete.length !== 1 ? "s" : ""}? The selected posts will be removed from the Posts Planner.`,
      {
        title: "Delete Selected Posts",
        confirmText: "Delete",
        cancelText: "Cancel",
      }
    );

    if (!confirmed) return;

    setBulkDeleting(true);
    try {
      await deleteFacebookPosts(idsToDelete);
      const deletedSet = new Set(idsToDelete);
      setPosts((prev) => prev.filter((p) => !deletedSet.has(p.id)));
      setSelectedPostIds(new Set());
      setNotification({
        type: "success",
        message: `${idsToDelete.length} post${idsToDelete.length !== 1 ? "s" : ""} deleted successfully!`,
      });
      setTimeout(() => setNotification(null), 3000);
    } catch (error) {
      console.error("Error bulk deleting posts:", error);
      alert(`Failed to delete selected posts: ${error.message}`);
    } finally {
      setBulkDeleting(false);
    }
  };

  // View post on Facebook
  const handleViewPost = (post) => {
    if (post.facebook_post_id) {
      window.open(`https://facebook.com/${post.facebook_post_id}`, '_blank');
    } else {
      alert("No Facebook post URL available for this post.");
    }
  };

  // Open edit modal
  const handleEditPost = (post) => {
    setEditingPost(post);
    setEditContent(post.message || post.content || "");
    setEditFirstComment(post.first_comment || "");
    setEditScheduledAt(
      post.scheduled_at ? new Date(post.scheduled_at).toISOString().slice(0, 16) : ""
    );
    setShowEditModal(true);
  };

  // Save edited post
  const handleSaveEdit = async () => {
    if (!editContent.trim()) {
      alert("Post content cannot be empty");
      return;
    }
    setEditSaving(true);
    try {
      const { error } = await supabase
        .from("fb_scheduled_posts")
        .update({
          message: editContent,
          first_comment: editFirstComment || null,
          scheduled_at: editScheduledAt || null,
        })
        .eq("id", editingPost.id);

      if (error) throw error;

      setShowEditModal(false);
      setEditingPost(null);
      await loadPosts();
      setNotification({ type: "success", message: "Post updated successfully!" });
      setTimeout(() => setNotification(null), 3000);
    } catch (err) {
      console.error("Error updating post:", err);
      alert(`Failed to update post: ${err.message}`);
    } finally {
      setEditSaving(false);
    }
  };

  // Reset modal form
  const closeModal = () => {
    startArticlesRequest();
    setShowModal(false);
    setPostContent("");
    setFirstComment("");
    setSelectedImages([]);
    setImagePreviews([]);
    setPostNow(true);
    setScheduledTime("");
    setSelectedPages([]);
    setScheduleError("");
    setSelectedWebsiteId("");
    setSelectedArticleId("");
    setWebsiteArticles([]);
    setPrefillImageUrls([]);
    setArticlesError("");
    setArticlesLoading(false);
    setArticleOffset(1);
    setHasMoreArticles(false);
    // bulk reset
    setBulkQueue([]);
    setPublishStartDate("");
    setBulkInterval(110);
    setBulkPageIds([]);
    setBulkSubmitting(false);
    setBulkPublishing(false);
    bulkPublishingRef.current = false;
    setBulkPostMode('withLink');
  };

  // ── Bulk Scheduler Handlers ──────────────────────────────────────

  const generateBulkSchedule = () => {
    if (bulkQueue.length === 0 || !publishStartDate) return;
    const effectiveInterval = fbPostsPerDay > 0 ? Math.round((24 * 60) / fbPostsPerDay) : bulkInterval;
    const firstPostDate = new Date(publishStartDate);
    if (Number.isNaN(firstPostDate.getTime())) return;
    const followingSchedule = generateHumanSchedule({
      platform: "facebook",
      count: Math.max(0, bulkQueue.length - 1),
      baseDate: new Date(firstPostDate.getTime() + effectiveInterval * 60000),
      intervalMinutes: effectiveInterval,
    });
    const schedule = [firstPostDate, ...followingSchedule];
    setBulkQueue((prev) =>
      prev.map((item, i) => ({ ...item, scheduledTime: toDatetimeLocalValue(schedule[i]) }))
    );
  };

  const handleBulkArticleToggle = async (article) => {
    const alreadyInQueue = bulkQueue.some(
      (item) => item.article.articleUrl === article.articleUrl
    );
    if (alreadyInQueue) {
      setBulkQueue((prev) =>
        prev.filter((item) => item.article.articleUrl !== article.articleUrl)
      );
      return;
    }

    const queueId = `${article.articleUrl}-${Date.now()}`;

    // ── Full Recipe mode: fetch JSON-LD recipe data from the article page ──
    if (bulkPostMode === 'fullRecipe') {
      setBulkQueue((prev) => [
        ...prev,
        {
          id: queueId,
          article,
          postContent: article.title,
          firstComment: "",
          imageUrl: article.featuredImageUrl || "",
          scheduledTime: "",
          loading: true,
        },
      ]);

      try {
        let recipeContent = null;

        // 1. Try WP REST API (slug lookup — no CORS issue)
        try {
          const url = new URL(article.articleUrl);
          const slug = url.pathname.split('/').filter(Boolean).pop() || '';
          if (slug) {
            const controller = new AbortController();
            const tid = setTimeout(() => controller.abort(), 5000);
            const wpRes = await fetch(
              `${url.origin}/wp-json/wp/v2/posts?slug=${slug}&_fields=content`,
              { signal: controller.signal }
            );
            clearTimeout(tid);
            if (wpRes.ok) {
              const posts = await wpRes.json();
              const html = posts?.[0]?.content?.rendered || '';
              const recipe = extractRecipeJsonLd(html);
              if (recipe) recipeContent = formatRecipePost(article.title, recipe);
            }
          }
        } catch {}

        // 2. Fallback: CORS proxy to fetch the article page HTML
        if (!recipeContent) {
          const controller = new AbortController();
          const tid = setTimeout(() => controller.abort(), 7000);
          const proxyRes = await fetch(
            `https://api.allorigins.win/raw?url=${encodeURIComponent(article.articleUrl)}`,
            { signal: controller.signal }
          );
          clearTimeout(tid);
          if (proxyRes.ok) {
            const html = await proxyRes.text();
            const recipe = extractRecipeJsonLd(html);
            if (recipe) recipeContent = formatRecipePost(article.title, recipe);
          }
        }

        setBulkQueue((prev) =>
          prev.map((item) =>
            item.id === queueId
              ? { ...item, postContent: recipeContent || article.title, loading: false }
              : item
          )
        );
      } catch {
        setBulkQueue((prev) =>
          prev.map((item) =>
            item.id === queueId ? { ...item, loading: false } : item
          )
        );
      }
      return;
    }

    // ── With Link mode: Post Content / First Comment generated via the same
    // AI logic as the Threads Caption / First Comment (with link) fields ──
    const initialContent = (article.recipeCardDescription || article.title || "").trim();

    setBulkQueue((prev) => [
      ...prev,
      {
        id: queueId,
        article,
        postContent: initialContent,
        firstComment: article.articleUrl || "",
        imageUrl: article.featuredImageUrl || "",
        scheduledTime: "",
        loading: true,
      },
    ]);

    if (functionsUrl) {
      try {
        const token = (await supabase.auth.getSession())?.data?.session?.access_token;
        const res = await fetch(`${functionsUrl}/generate-facebook-content`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            apikey: supabaseAnonKey,
          },
          body: JSON.stringify({
            title: article.title,
            keyword: article.focusKeyword,
            excerpt: article.recipeCardDescription,
            articleUrl: article.articleUrl,
            language: article.language || 'en',
          }),
        });
        const data = await res.json().catch(() => ({}));
        setBulkQueue((prev) =>
          prev.map((item) =>
            item.id === queueId
              ? {
                  ...item,
                  postContent: item.postContent,
                  firstComment: (res.ok && data.first_comment) || article.articleUrl || "",
                  loading: false,
                }
              : item
          )
        );
      } catch (e) {
        console.error("[FacebookPlanner] generate-facebook-content:", e);
        setBulkQueue((prev) =>
          prev.map((item) =>
            item.id === queueId ? { ...item, loading: false } : item
          )
        );
      }
    } else {
      setBulkQueue((prev) =>
        prev.map((item) =>
          item.id === queueId ? { ...item, loading: false } : item
        )
      );
    }
  };

  const handleBulkQueueUpdate = (id, field, value) => {
    setBulkQueue((prev) =>
      prev.map((item) => (item.id === id ? { ...item, [field]: value } : item))
    );
  };

  const handleBulkRemoveRow = (id) => {
    setBulkQueue((prev) => prev.filter((item) => item.id !== id));
  };

  const toggleBulkPage = (pageId) => {
    setBulkPageIds((prev) =>
      prev.includes(pageId) ? prev.filter((id) => id !== pageId) : [...prev, pageId]
    );
  };

  const handleBulkPublishNow = async () => {
    if (bulkPublishingRef.current || bulkSubmitting || bulkQueue.length === 0 || bulkPageIds.length === 0) return;
    const emptyContent = bulkQueue.filter((item) => !item.postContent?.trim());
    if (emptyContent.length > 0) {
      alert(`${emptyContent.length} post${emptyContent.length !== 1 ? "s have" : " has"} no content. Add post content before publishing.`);
      return;
    }

    bulkPublishingRef.current = true;
    setBulkPublishing(true);
    let succeeded = 0;
    let failed = 0;

    try {
      for (const item of bulkQueue) {
        const alreadyPublished = new Set(item.publishedPageIds || []);
        const targetPageIds = bulkPageIds.filter((pageId) => !alreadyPublished.has(pageId));
        if (targetPageIds.length === 0) continue;

        setBulkQueue((current) => current.map((row) => row.id === item.id
          ? { ...row, publishStatus: "publishing", publishMessage: "Publishing…" }
          : row));

        let results = [];
        try {
          const response = await createFacebookPost({
            profileId: activeProfile?.id || null,
            content: item.postContent,
            firstComment: item.firstComment,
            imageUrls: item.imageUrl ? [item.imageUrl] : [],
            articleLink: item.article?.articleUrl || "",
            pageIds: targetPageIds,
            postNow: true,
          });
          results = response?.results || [];
        } catch (error) {
          results = error.results || [];
          if (results.length === 0) {
            results = targetPageIds.map((pageId) => ({ page_id: pageId, success: false, error: error.message }));
          }
        }

        const successfulPageIds = results.filter((result) => result.success).map((result) => result.page_id);
        const warnings = results.filter((result) => result.success && result.warning).map((result) => result.warning);
        const failures = results.filter((result) => !result.success);
        succeeded += successfulPageIds.length;
        failed += failures.length;
        const nextPublishedPageIds = [...new Set([...(item.publishedPageIds || []), ...successfulPageIds])];
        const publishMessage = failures.length
          ? `${successfulPageIds.length}/${targetPageIds.length} Pages published. ${failures.map((result) => result.error).join("; ")}`
          : `Published to ${successfulPageIds.length} Page${successfulPageIds.length !== 1 ? "s" : ""}.`;

        setBulkQueue((current) => current.map((row) => row.id === item.id
          ? {
              ...row,
              publishedPageIds: nextPublishedPageIds,
              publishStatus: failures.length ? "error" : "success",
              publishMessage: [publishMessage, ...warnings].join(" "),
            }
          : row));
      }

      await loadPosts();
      setNotification({
        type: failed > 0 ? "error" : "success",
        message: failed > 0
          ? `Published ${succeeded} post${succeeded !== 1 ? "s" : ""}; ${failed} failed. See each article for details.`
          : `Published ${succeeded} post${succeeded !== 1 ? "s" : ""} successfully.`,
      });
      setTimeout(() => setNotification(null), 5000);
    } finally {
      bulkPublishingRef.current = false;
      setBulkPublishing(false);
    }
  };

  // Submits the whole bulk queue as one durable background batch and
  // returns immediately — it does NOT loop over items and call
  // facebook-post itself. That execution now happens server-side (Vercel
  // Queue push mode -> a consumer Vercel invokes automatically -> the same
  // unmodified facebook-post function), so it keeps running regardless of
  // whether this modal, this page, or this browser tab still exists by the
  // time it finishes. See api/fb-bulk-schedule/create.js and
  // api/queues/fb-bulk-schedule-consumer.js.
  const handleBulkSubmit = async () => {
    if (bulkQueue.length === 0) {
      alert("Please select at least one article");
      return;
    }
    if (bulkPageIds.length === 0) {
      alert("Please select at least one Facebook page");
      return;
    }
    const unscheduled = bulkQueue.filter((item) => !item.scheduledTime);
    if (unscheduled.length > 0) {
      alert(`${unscheduled.length} post(s) have no scheduled time. Click "Generate Schedule" first.`);
      return;
    }
    const emptyContent = bulkQueue.filter((item) => !item.postContent?.trim());
    if (emptyContent.length > 0) {
      alert(`${emptyContent.length} post${emptyContent.length !== 1 ? "s have" : " has"} no content. Add post content before scheduling.`);
      return;
    }

    const queueSnapshot = [...bulkQueue];
    const pageIdsSnapshot = [...bulkPageIds];

    setBulkSubmitting(true);

    try {
      // Client-generated so a network retry of this exact request can never
      // create a second batch or duplicate queue rows — see the
      // upsert+ignoreDuplicates handling in api/fb-bulk-schedule/create.js.
      const batchId = crypto.randomUUID();
      const items = queueSnapshot.map((item) => ({
        articleId: item.article?.id || null,
        content: item.postContent,
        firstComment: item.firstComment,
        imageUrl: item.imageUrl || null,
        articleLink: item.article?.articleUrl || "",
        scheduledTime: new Date(item.scheduledTime).toISOString(),
      }));

      const { totalCount } = await createBulkScheduleBatch(
        batchId,
        items,
        pageIdsSnapshot,
        activeProfile?.id || null
      );

      // From here on the browser is a spectator: the batch and every queue
      // row already exist in Postgres and are dispatched to Vercel Queue.
      setSchedulingProgress({ active: true, batchId, done: 0, total: totalCount, failed: 0, status: "pending" });
      setNotification({
        type: "success",
        message: `${totalCount} post${totalCount !== 1 ? "s" : ""} queued for scheduling — running safely in the background.`,
      });
      setTimeout(() => setNotification(null), 4000);
      closeModal();
    } catch (err) {
      console.error("[FacebookPlanner] bulk schedule submit failed:", err);
      alert(err.message || "Failed to submit the bulk schedule. Please try again.");
      setBulkSubmitting(false);
    }
  };

  // Get status badge color
  const getStatusBadge = (status) => {
    const colors = {
      scheduled: "bg-blue-100 text-blue-700",
      published: "bg-green-100 text-green-700",
      draft: "bg-gray-100 text-gray-700",
      failed: "bg-red-100 text-red-700",
    };
    return colors[status] || "bg-gray-100 text-gray-700";
  };

  // Format date — compact
  const formatDateTime = (dateStr) => {
    if (!dateStr) return "—";
    const date = new Date(dateStr);
    return date.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  // Truncate content
  const truncateContent = (content, maxLength = 100) => {
    if (!content) return "—";
    return content.length > maxLength
      ? content.substring(0, maxLength) + "..."
      : content;
  };

  return (
    <SidebarProvider>
      <div className="min-h-screen bg-background overflow-x-hidden md:overflow-x-visible">
        <Sidebar />
        
        <main className="main-content">
          {/* Notification */}
          {notification && (
            <div className="fixed top-3 left-3 right-3 sm:top-4 sm:left-auto sm:right-4 z-50 animate-fade-in">
              <div className={`${notification.type === "error" ? "bg-red-500" : "bg-green-500"} text-white px-4 sm:px-6 py-3 rounded-lg shadow-lg flex items-center gap-2`}>
                {notification.type === "error" ? <AlertCircle className="w-5 h-5" /> : <Check className="w-5 h-5" />}
                {notification.message}
              </div>
            </div>
          )}

          <div className="p-3 sm:p-6 space-y-6">
            {/* Header */}
            <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-4 min-w-0">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-white" />
                  </div>
                  <div className="min-w-0">
                    <h1 className="text-xl font-bold text-gray-900">
                      Facebook Posts Planner
                    </h1>
                    <p className="text-sm text-gray-500">
                      Schedule and manage your Facebook posts across multiple pages
                    </p>
                  </div>
                </div>
                
                <button
                  onClick={() => setShowModal(true)}
                  className="w-full sm:w-auto justify-center inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-medium text-sm hover:from-indigo-700 hover:to-purple-700 transition-all shadow-md hover:shadow-lg"
                >
                  <Plus className="w-5 h-5" />
                  New Post
                </button>
              </div>
            </div>

            {/* Scheduling Progress Bar — reads the persistent fb_bulk_schedule_batches
                row (see the useEffect above), so it shows the real backend
                state on load, after a refresh, or after returning from
                another page, never a locally-driven loop. */}
            {schedulingProgress.active && (
              <div className="bg-white border border-gray-200 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-gray-700">
                    {schedulingProgress.status === "completed"
                      ? `✅ Facebook scheduling completed — ${schedulingProgress.total}/${schedulingProgress.total} processed`
                      : schedulingProgress.status === "completed_with_errors"
                      ? `⚠️ Facebook scheduling completed — ${schedulingProgress.total - schedulingProgress.failed} successful, ${schedulingProgress.failed} failed`
                      : "Scheduling Progress"}
                  </span>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-bold text-green-600">
                      {schedulingProgress.done}/{schedulingProgress.total}
                    </span>
                    {(schedulingProgress.status === "completed" || schedulingProgress.status === "completed_with_errors") && (
                      <button
                        onClick={handleDismissSchedulingProgress}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                        aria-label="Dismiss"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-4 overflow-hidden">
                  <div
                    className={`h-4 rounded-full transition-all duration-500 ease-out ${
                      schedulingProgress.status === "completed_with_errors" ? "bg-amber-500" : "bg-green-500"
                    }`}
                    style={{
                      width: `${schedulingProgress.total > 0 ? (schedulingProgress.done / schedulingProgress.total) * 100 : 0}%`
                    }}
                  />
                </div>
                {schedulingProgress.failed > 0 && (
                  <button
                    onClick={() => {
                      setStatusFilter("failed");
                      handleFilterChange();
                    }}
                    className="mt-2 text-xs font-medium text-amber-700 hover:text-amber-900 underline"
                  >
                    View {schedulingProgress.failed} failed item{schedulingProgress.failed !== 1 ? "s" : ""}
                  </button>
                )}
              </div>
            )}

            {/* Filters — same design as Pinterest / Pin Library */}
            <div className="bg-white border border-gray-200/80 rounded-2xl shadow-sm p-3 sm:p-5">
              {/* Row: search + page + dates + actions */}
              <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:items-center">
                {/* Search */}
                <div className="relative w-full sm:flex-1 sm:min-w-[220px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search posts by content or comment…"
                    className="w-full h-10 pl-9 pr-4 rounded-xl border border-gray-200 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>

                {/* Page filter */}
                <div className="relative w-full sm:w-auto sm:min-w-[180px]">
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-indigo-500 pointer-events-none" />
                  <select
                    value={pageFilter}
                    onChange={(e) => {
                      setPageFilter(e.target.value);
                      handleFilterChange();
                    }}
                    className="w-full h-10 rounded-xl border border-gray-200 pl-9 pr-8 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white appearance-none"
                  >
                    <option value="">All Pages</option>
                    {pages.map((page) => (
                      <option key={page.page_id} value={page.page_id}>
                        {page.page_name}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                </div>

                {/* Date from */}
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <span className="text-xs text-gray-400 whitespace-nowrap">From</span>
                  <input
                    type="date"
                    value={dateRange.start}
                    onChange={(e) => {
                      setDateRange((prev) => ({ ...prev, start: e.target.value }));
                      handleFilterChange();
                    }}
                    className="flex-1 min-w-0 sm:flex-none h-10 rounded-xl border border-gray-200 px-3 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>

                {/* Date to */}
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <span className="text-xs text-gray-400 whitespace-nowrap">To</span>
                  <input
                    type="date"
                    value={dateRange.end}
                    onChange={(e) => {
                      setDateRange((prev) => ({ ...prev, end: e.target.value }));
                      handleFilterChange();
                    }}
                    className="flex-1 min-w-0 sm:flex-none h-10 rounded-xl border border-gray-200 px-3 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>

                {/* Clear filters */}
                {(searchQuery || dateRange.start || dateRange.end || (statusFilter && statusFilter !== "all") || pageFilter) && (
                  <button
                    type="button"
                    onClick={handleResetFilters}
                    className="w-full sm:w-auto h-10 px-3 rounded-xl border border-gray-200 text-xs text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition-colors whitespace-nowrap"
                  >
                    Clear filters
                  </button>
                )}

                {/* Delete Selected Button */}
                <button
                  onClick={handleBulkDeleteSelected}
                  disabled={selectedPostIds.size === 0 || bulkDeleting}
                  className="w-full sm:w-auto justify-center px-4 py-2 rounded-lg bg-red-50 text-red-600 text-sm hover:bg-red-100 transition-colors flex items-center gap-2 sm:ml-auto disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete Selected{selectedPostIds.size > 0 ? ` (${selectedPostIds.size})` : ""}
                </button>

                {/* Refresh Button */}
                <button
                  onClick={loadPosts}
                  disabled={postsLoading}
                  className="w-full sm:w-auto justify-center px-4 py-2 rounded-lg bg-indigo-50 text-indigo-600 text-sm hover:bg-indigo-100 transition-colors flex items-center gap-2"
                >
                  <RefreshCw className={`w-4 h-4 ${postsLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
              </div>

              {/* Status pills */}
              <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-gray-100">
                {[
                  { key: "all", label: "All", count: statusCounts.all, dot: "bg-gray-400", active: "bg-gray-900 text-white shadow-sm", inactive: "bg-gray-100 text-gray-600 hover:bg-gray-200" },
                  { key: "scheduled", label: "Scheduled", count: statusCounts.scheduled, dot: "bg-blue-500", active: "bg-blue-600 text-white shadow-sm", inactive: "bg-blue-50 text-blue-700 hover:bg-blue-100" },
                  { key: "published", label: "Published", count: statusCounts.published, dot: "bg-emerald-500", active: "bg-emerald-600 text-white shadow-sm", inactive: "bg-emerald-50 text-emerald-700 hover:bg-emerald-100" },
                  { key: "draft", label: "Draft", count: statusCounts.draft, dot: "bg-slate-400", active: "bg-slate-600 text-white shadow-sm", inactive: "bg-slate-100 text-slate-600 hover:bg-slate-200" },
                  { key: "failed", label: "Failed", count: statusCounts.failed, dot: "bg-red-500", active: "bg-red-600 text-white shadow-sm", inactive: "bg-red-50 text-red-700 hover:bg-red-100" },
                ].map(({ key, label, count, dot, active, inactive }) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => {
                      setStatusFilter(key);
                      handleFilterChange();
                    }}
                    className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-sm font-medium transition-all duration-150 ${statusFilter === key ? active : inactive}`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${statusFilter === key ? "bg-current opacity-60" : dot}`} />
                    {label}
                    <span className={`text-xs font-semibold tabular-nums ${statusFilter === key ? "opacity-75" : "opacity-60"}`}>
                      {count ?? 0}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Posts Table */}
            {(() => {
              const totalPages = totalTablePages;

              // build visible page numbers (max 5 around current)
              const pageNums = [];
              const delta = 2;
              for (let i = Math.max(1, safeTablePage - delta); i <= Math.min(totalPages, safeTablePage + delta); i++) {
                pageNums.push(i);
              }

              return (
                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                  {loading ? (
                    <div className="flex items-center justify-center h-64">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
                    </div>
                  ) : searchedPosts.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                      <Calendar className="w-12 h-12 text-gray-300 mb-3" />
                      <p className="text-lg font-medium">No posts found</p>
                      <p className="text-sm">Create your first post to get started</p>
                    </div>
                  ) : (
                    <>
                      <div className="overflow-x-auto">
                        <table className="w-full min-w-[900px] md:min-w-0 border-collapse">
                          <thead>
                            <tr className="bg-gray-50 border-b border-gray-200">
                              {/* Thumbnail */}
                              <th className="w-14 px-3 py-2.5 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider" />
                              {/* Post Content */}
                              <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                                Post Content
                              </th>
                              {/* First Comment */}
                              <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                                First Comment
                              </th>
                              {/* Page */}
                              <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                                Page
                              </th>
                              {/* Status */}
                              <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                                Status
                              </th>
                              {/* Select All */}
                              <th className="px-3 py-2.5 text-center text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                                <input
                                  ref={selectAllRef}
                                  type="checkbox"
                                  checked={isAllOnPageSelected}
                                  onChange={toggleSelectAllOnPage}
                                  className="h-4 w-4 cursor-pointer rounded accent-indigo-600"
                                  aria-label="Select all visible posts"
                                />
                              </th>
                              {/* Post Link */}
                              <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                                Post Link
                              </th>
                              {/* Scheduled */}
                              <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider whitespace-nowrap">
                                Scheduled
                              </th>
                              {/* Actions */}
                              <th className="px-3 py-2.5 text-center text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {pagedPosts.map((post, idx) => (
                              <tr
                                key={post.id}
                                className={`group border-b border-gray-100 transition-colors hover:bg-indigo-50/40 ${idx % 2 === 0 ? "bg-white" : "bg-gray-50/40"}`}
                              >
                                {/* Thumbnail */}
                                <td className="w-14 px-3 py-2">
                                  <div className="w-11 h-11 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0 flex items-center justify-center">
                                    {post.image_url ? (
                                      <img
                                        src={post.image_url}
                                        alt=""
                                        className="w-full h-full object-cover"
                                      />
                                    ) : (
                                      <ImageIcon className="w-4 h-4 text-gray-300" />
                                    )}
                                  </div>
                                </td>

                                {/* Post Content */}
                                <td className="px-3 py-2 max-w-[180px]">
                                  <p
                                    className="text-sm text-gray-800 font-medium whitespace-nowrap overflow-hidden text-ellipsis"
                                    style={{ maxWidth: 170 }}
                                    title={post.message || post.content || ""}
                                  >
                                    {truncateContent(post.message || post.content, 28)}
                                  </p>
                                </td>

                                {/* First Comment */}
                                <td className="px-3 py-2 max-w-[160px]">
                                  <p
                                    className="text-xs text-gray-500 whitespace-nowrap overflow-hidden text-ellipsis"
                                    style={{ maxWidth: 150 }}
                                    title={post.first_comment || ""}
                                  >
                                    {truncateContent(post.first_comment, 35) || <span className="text-gray-300">—</span>}
                                  </p>
                                </td>

                                {/* Page */}
                                <td className="px-3 py-2">
                                  <div className="flex items-center gap-1.5 whitespace-nowrap">
                                    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center text-indigo-600 font-semibold text-[10px] flex-shrink-0">
                                      {post.page_name?.charAt(0) || "P"}
                                    </div>
                                    <span
                                      className="text-xs text-gray-700 whitespace-nowrap overflow-hidden text-ellipsis"
                                      style={{ maxWidth: 100 }}
                                      title={post.page_name || ""}
                                    >
                                      {post.page_name || "Unknown"}
                                    </span>
                                  </div>
                                </td>

                                {/* Status */}
                                <td className="px-3 py-2">
                                  <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap ${getStatusBadge(post.status)}`}>
                                    {post.status?.charAt(0).toUpperCase() + post.status?.slice(1)}
                                  </span>
                                </td>

                                {/* Select */}
                                <td className="px-3 py-2 text-center">
                                  <input
                                    type="checkbox"
                                    checked={selectedPostIds.has(post.id)}
                                    onChange={() => toggleSelectPost(post.id)}
                                    className="h-4 w-4 cursor-pointer rounded accent-indigo-600"
                                    aria-label={`Select post ${post.id}`}
                                  />
                                </td>

                                {/* Post Link */}
                                <td className="px-3 py-2">
                                  {post.facebook_post_id ? (
                                    <a
                                      href={`https://facebook.com/${post.facebook_post_id}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center gap-1 text-xs text-indigo-500 hover:text-indigo-700 whitespace-nowrap"
                                    >
                                      <LinkIcon className="w-3 h-3" />
                                      View
                                    </a>
                                  ) : (
                                    <span className="text-xs text-gray-300">—</span>
                                  )}
                                </td>

                                {/* Scheduled */}
                                <td className="px-3 py-2 whitespace-nowrap">
                                  <span className="text-xs text-gray-500">
                                    {formatDateTime(post.scheduled_at || post.scheduled_time)}
                                  </span>
                                </td>

                                {/* Actions */}
                                <td className="px-3 py-2">
                                  <div className="flex items-center justify-center gap-0.5">
                                    <button
                                      onClick={() => handleViewPost(post)}
                                      className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors"
                                      title="View on Facebook"
                                    >
                                      <Eye className="w-3.5 h-3.5" />
                                    </button>
                                    <button
                                      onClick={() => handleEditPost(post)}
                                      disabled={post.status === "published" || post.status === "failed"}
                                      className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                      title={
                                        post.status === "published"
                                          ? "Published posts cannot be edited"
                                          : post.status === "failed"
                                          ? "Failed posts cannot be edited"
                                          : "Edit post"
                                      }
                                    >
                                      <Edit className="w-3.5 h-3.5" />
                                    </button>
                                    <button
                                      onClick={() => handleDeletePost(post.id)}
                                      className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                                      title="Delete"
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Pagination */}
                      {totalPages > 1 && (
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 px-4 py-3 border-t border-gray-100 bg-gray-50/60">
                          <p className="text-xs text-gray-400">
                            Showing {(safeTablePage - 1) * TABLE_PAGE_SIZE + 1}–{Math.min(safeTablePage * TABLE_PAGE_SIZE, searchedPosts.length)} of {searchedPosts.length} posts
                          </p>
                          <div className="flex items-center gap-1 w-full sm:w-auto overflow-x-auto">
                            {/* Previous */}
                            <button
                              onClick={() => setTablePage((p) => Math.max(1, p - 1))}
                              disabled={safeTablePage === 1}
                              className="flex items-center gap-1 px-2.5 py-1.5 text-xs text-gray-600 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                            >
                              <ChevronLeft className="w-3.5 h-3.5" />
                              Prev
                            </button>

                            {/* First page + ellipsis */}
                            {pageNums[0] > 1 && (
                              <>
                                <button
                                  onClick={() => setTablePage(1)}
                                  className="px-2.5 py-1.5 text-xs rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-gray-600 transition-colors"
                                >
                                  1
                                </button>
                                {pageNums[0] > 2 && <span className="px-1 text-xs text-gray-400">…</span>}
                              </>
                            )}

                            {/* Page numbers */}
                            {pageNums.map((n) => (
                              <button
                                key={n}
                                onClick={() => setTablePage(n)}
                                className={`px-2.5 py-1.5 text-xs rounded-lg border transition-colors ${
                                  n === safeTablePage
                                    ? "bg-indigo-600 border-indigo-600 text-white font-semibold"
                                    : "border-gray-200 bg-white hover:bg-gray-50 text-gray-600"
                                }`}
                              >
                                {n}
                              </button>
                            ))}

                            {/* Last page + ellipsis */}
                            {pageNums[pageNums.length - 1] < totalPages && (
                              <>
                                {pageNums[pageNums.length - 1] < totalPages - 1 && (
                                  <span className="px-1 text-xs text-gray-400">…</span>
                                )}
                                <button
                                  onClick={() => setTablePage(totalPages)}
                                  className="px-2.5 py-1.5 text-xs rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-gray-600 transition-colors"
                                >
                                  {totalPages}
                                </button>
                              </>
                            )}

                            {/* Next */}
                            <button
                              onClick={() => setTablePage((p) => Math.min(totalPages, p + 1))}
                              disabled={safeTablePage === totalPages}
                              className="flex items-center gap-1 px-2.5 py-1.5 text-xs text-gray-600 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                            >
                              Next
                              <ChevronRight className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })()}
          </div>
        </main>

        {/* Bulk Post Scheduler Modal */}
        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 overflow-hidden">
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={closeModal}
            />

            {/* Modal Container */}
            <div className="relative w-full md:w-[95vw] max-w-[1600px] h-[calc(100dvh-1rem)] md:h-[92vh] bg-white rounded-xl md:rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-fade-in">

              {/* ── Header ── */}
              <div className="flex-none border-b border-gray-100 px-3 py-3 md:px-6 md:py-4 flex items-center justify-between bg-white">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                    <Layers className="w-5 h-5 text-white" />
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-lg font-semibold text-gray-900 leading-tight">Bulk Post Scheduler</h2>
                    <p className="text-xs text-gray-400 truncate">Select articles → set interval → generate schedule → publish</p>
                  </div>
                </div>
                <button
                  onClick={closeModal}
                  className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors flex-shrink-0"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* ── Body: Left article browser + Right scheduler ── */}
              <div className="flex-1 min-h-0 flex flex-col md:flex-row overflow-y-auto md:overflow-hidden">

                {/* Left Panel — Article Browser */}
                <div className="w-full h-64 md:w-72 md:h-auto flex-none border-b md:border-b-0 md:border-r border-gray-100 flex flex-col overflow-hidden bg-gray-50/60">
                  {/* Website selector */}
                  <div className="flex-none p-4 border-b border-gray-100 bg-white">
                    <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Website</p>
                    <select
                      value={selectedWebsiteId}
                      onChange={(e) => handleWebsiteChange(e.target.value)}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    >
                      <option value="">-- Select a website --</option>
                      {websites.map((site) => (
                        <option key={site.id} value={site.id}>{site.siteName}</option>
                      ))}
                    </select>
                    {bulkQueue.length > 0 && (
                      <p className="mt-2 text-[11px] text-indigo-600 font-medium">
                        {bulkQueue.length} article{bulkQueue.length !== 1 ? "s" : ""} selected
                      </p>
                    )}
                  </div>

                  {/* Article list */}
                  <div className="flex-1 overflow-y-auto p-3 space-y-2">
                    {!selectedWebsiteId ? (
                      <div className="flex flex-col items-center justify-center h-full text-center px-4 gap-2">
                        <Globe className="w-8 h-8 text-gray-200" />
                        <p className="text-sm text-gray-400">Select a website to browse articles</p>
                      </div>
                    ) : articlesLoading && websiteArticles.length === 0 ? (
                      <div className="flex items-center justify-center py-10 gap-2 text-indigo-400 text-sm">
                        <div className="w-4 h-4 border-2 border-indigo-300 border-t-indigo-500 rounded-full animate-spin" />
                        Loading…
                      </div>
                    ) : articlesError && websiteArticles.length === 0 ? (
                      <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700">
                        <p className="font-medium">Failed to load articles</p>
                        <p className="mt-1 break-words">{articlesError}</p>
                        <button
                          type="button"
                          onClick={() => loadArticles(selectedWebsiteId, 1, false)}
                          className="mt-2 font-medium underline"
                        >
                          Try again
                        </button>
                      </div>
                    ) : websiteArticles.length === 0 ? (
                      <p className="text-xs text-gray-400 text-center py-8">No published WordPress articles found for this site</p>
                    ) : (
                      <>
                        {articlesError && (
                          <div className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700 break-words">
                            {articlesError}
                          </div>
                        )}
                        {/* Select All */}
                        <div className="flex items-center gap-2 px-1 pb-2 border-b border-gray-100">
                          <input
                            type="checkbox"
                            id="bulk-select-all"
                            checked={
                              websiteArticles.length > 0 &&
                              websiteArticles.every((a) =>
                                bulkQueue.some((item) => item.article.articleUrl === a.articleUrl)
                              )
                            }
                            onChange={(e) => {
                              if (e.target.checked) {
                                websiteArticles.forEach((a) => {
                                  if (!bulkQueue.some((item) => item.article.articleUrl === a.articleUrl)) {
                                    handleBulkArticleToggle(a);
                                  }
                                });
                              } else {
                                setBulkQueue((prev) =>
                                  prev.filter(
                                    (item) => !websiteArticles.some((a) => a.articleUrl === item.article.articleUrl)
                                  )
                                );
                              }
                            }}
                            className="w-4 h-4 accent-indigo-500 cursor-pointer"
                          />
                          <label htmlFor="bulk-select-all" className="text-xs font-medium text-gray-600 cursor-pointer select-none">
                            Select All
                          </label>
                        </div>

                        {websiteArticles.map((article) => {
                          const inQueue = bulkQueue.some(
                            (item) => item.article.articleUrl === article.articleUrl
                          );
                          return (
                            <button
                              key={article.articleUrl}
                              type="button"
                              onClick={() => handleBulkArticleToggle(article)}
                              className={`w-full flex gap-2.5 p-2.5 rounded-xl border-2 text-left transition-all hover:shadow-sm focus:outline-none ${
                                inQueue
                                  ? "border-indigo-500 bg-indigo-50 shadow-sm"
                                  : "border-gray-200 bg-white hover:border-indigo-200"
                              }`}
                            >
                              {/* Thumbnail */}
                              <div className="w-11 h-11 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0 flex items-center justify-center">
                                {article.featuredImageUrl ? (
                                  <img
                                    src={article.featuredImageUrl}
                                    alt=""
                                    className="w-full h-full object-cover"
                                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                                  />
                                ) : (
                                  <ImageIcon className="w-4 h-4 text-gray-300" />
                                )}
                              </div>
                              {/* Title */}
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium text-gray-800 line-clamp-2 leading-snug">
                                  {article.title}
                                </p>
                                {article.publishDate && (
                                  <p className="text-[10px] text-gray-400 mt-0.5">
                                    {new Date(article.publishDate).toLocaleDateString()}
                                  </p>
                                )}
                              </div>
                              {/* Check badge */}
                              {inQueue && (
                                <div className="flex-none w-5 h-5 bg-indigo-500 rounded-full flex items-center justify-center self-center">
                                  <Check className="w-3 h-3 text-white" />
                                </div>
                              )}
                            </button>
                          );
                        })}

                        {/* Load more */}
                        {hasMoreArticles && (
                          <button
                            type="button"
                            onClick={handleShowMoreArticles}
                            disabled={articlesLoading}
                            className="w-full py-2 text-xs text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50 disabled:opacity-40 flex items-center justify-center gap-1 transition-colors"
                          >
                            {articlesLoading ? (
                              <div className="w-3 h-3 border border-indigo-400 border-t-transparent rounded-full animate-spin" />
                            ) : "Load more articles"}
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Right Panel — Scheduler */}
                <div className="w-full min-w-0 flex-none md:flex-1 flex flex-col overflow-visible md:overflow-hidden">

                  {/* Controls bar */}
                  <div className="flex-none border-b border-gray-100 px-3 md:px-5 py-3 bg-white flex flex-col items-stretch gap-3 md:flex-row md:flex-wrap md:items-center md:gap-x-5 md:gap-y-2">
                    <div className="flex flex-col items-stretch gap-1 md:flex-row md:items-center md:gap-2">
                      <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <label className="text-xs font-medium text-gray-600 whitespace-nowrap">Publish Start Date</label>
                      <input
                        type="datetime-local"
                        step={60}
                        value={publishStartDate}
                        onChange={(e) => setPublishStartDate(e.target.value)}
                        className="w-full min-w-0 md:w-auto border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>

                    <div className="flex flex-col items-stretch gap-1 md:flex-row md:items-center md:gap-2">
                      <RefreshCw className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <label className="text-xs font-medium text-gray-600 whitespace-nowrap">Posts Per Day</label>
                      <select
                        value={fbPostsPerDay}
                        onChange={(e) => setFbPostsPerDay(Number(e.target.value))}
                        className="w-full min-w-0 md:w-auto border border-gray-200 rounded-lg px-2 py-1.5 text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                      >
                        <option value={0}>Manual interval</option>
                        {Array.from({ length: 15 }, (_, i) => i + 1).map((n) => (
                          <option key={n} value={n}>
                            {n} post{n > 1 ? 's' : ''}/day — every {Math.round((24 * 60) / n)} min
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      <label className={`text-xs font-medium whitespace-nowrap ${fbPostsPerDay > 0 ? 'text-gray-400' : 'text-gray-600'}`}>Interval</label>
                      <input
                        type="number"
                        min={1}
                        value={fbPostsPerDay > 0 ? Math.round((24 * 60) / fbPostsPerDay) : bulkInterval}
                        onChange={(e) => setBulkInterval(Math.max(1, parseInt(e.target.value) || 1))}
                        disabled={fbPostsPerDay > 0}
                        className="w-16 border border-gray-200 rounded-lg px-2 py-1.5 text-sm text-center focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed"
                      />
                      <span className={`text-xs whitespace-nowrap ${fbPostsPerDay > 0 ? 'text-gray-400' : 'text-gray-400'}`}>min between posts</span>
                    </div>

                    {/* Post mode selection */}
                    <div className="flex flex-wrap items-center gap-4">
                      <label className="flex items-center gap-1.5 cursor-pointer select-none text-xs font-medium text-gray-600">
                        <input
                          type="checkbox"
                          checked={bulkPostMode === 'withLink'}
                          onChange={() => setBulkPostMode('withLink')}
                          className="w-3.5 h-3.5 accent-indigo-500 cursor-pointer"
                        />
                        With Link
                      </label>
                      <label className="flex items-center gap-1.5 cursor-pointer select-none text-xs font-medium text-gray-600">
                        <input
                          type="checkbox"
                          checked={bulkPostMode === 'fullRecipe'}
                          onChange={() => setBulkPostMode('fullRecipe')}
                          className="w-3.5 h-3.5 accent-indigo-500 cursor-pointer"
                        />
                        Full Recipe
                      </label>
                    </div>

                    <button
                      onClick={generateBulkSchedule}
                      disabled={bulkQueue.length === 0 || !publishStartDate}
                      className="w-full md:w-auto justify-center md:ml-auto inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      <Zap className="w-4 h-4" />
                      Generate Schedule
                    </button>
                  </div>

                  {/* Queue — empty state */}
                  {bulkQueue.length === 0 ? (
                    <div className="flex-1 flex flex-col items-center justify-center gap-3 text-center px-8">
                      <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center">
                        <Layers className="w-8 h-8 text-gray-300" />
                      </div>
                      <p className="text-sm font-medium text-gray-500">No articles selected yet</p>
                      <p className="text-xs text-gray-400 max-w-xs">
                        Click articles on the left to add them to the schedule. Each article becomes one row.
                      </p>
                    </div>
                  ) : (
                    <div className="flex-1 overflow-auto">
                      {/* Table header */}
                      <div className="min-w-[760px] md:min-w-0 sticky top-0 z-10 bg-gray-50 border-b border-gray-200 grid gap-3 px-4 py-2"
                        style={{ gridTemplateColumns: "56px 1fr 200px 165px 36px" }}>
                        <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider"></span>
                        <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Post Content</span>
                        <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">First Comment</span>
                        <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Scheduled Time</span>
                        <span></span>
                      </div>

                      {/* Rows */}
                      <div className="divide-y divide-gray-100">
                        {bulkQueue.map((item, idx) => (
                          <div
                            key={item.id}
                            className="min-w-[760px] md:min-w-0 grid gap-3 px-4 py-3 items-start hover:bg-indigo-50/20 transition-colors"
                            style={{ gridTemplateColumns: "56px 1fr 200px 165px 36px" }}
                          >
                            {/* Thumbnail + index */}
                            <div className="flex flex-col items-center gap-1 pt-0.5">
                              <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0 flex items-center justify-center">
                                {item.imageUrl ? (
                                  <img src={item.imageUrl} alt="" className="w-full h-full object-cover" />
                                ) : (
                                  <ImageIcon className="w-4 h-4 text-gray-300" />
                                )}
                              </div>
                              <span className="text-[10px] text-gray-400 font-semibold">#{idx + 1}</span>
                            </div>

                            {/* Post Content */}
                            <div>
                              {item.loading ? (
                                <div className="flex items-center gap-1.5 text-indigo-400 text-xs py-3">
                                  <div className="w-3 h-3 border-2 border-indigo-300 border-t-indigo-500 rounded-full animate-spin flex-shrink-0" />
                                  Fetching description…
                                </div>
                              ) : (
                                <textarea
                                  value={item.postContent}
                                  onChange={(e) => handleBulkQueueUpdate(item.id, "postContent", e.target.value)}
                                  rows={4}
                                  className="w-full text-xs border border-gray-200 rounded-lg px-2.5 py-2 resize-y focus:outline-none focus:ring-1 focus:ring-indigo-400 text-gray-700 leading-relaxed"
                                />
                              )}
                              {item.publishMessage && (
                                <p className={`mt-1 text-[10px] ${item.publishStatus === "error" ? "text-red-600" : item.publishStatus === "success" ? "text-green-600" : "text-indigo-500"}`}>
                                  {item.publishMessage}
                                </p>
                              )}
                            </div>

                            {/* First Comment */}
                            <div>
                              <textarea
                                value={item.firstComment}
                                onChange={(e) => handleBulkQueueUpdate(item.id, "firstComment", e.target.value)}
                                rows={4}
                                className="w-full text-xs border border-gray-200 rounded-lg px-2.5 py-2 resize-none focus:outline-none focus:ring-1 focus:ring-indigo-400 text-gray-400 leading-relaxed"
                                placeholder="Article link or comment…"
                              />
                            </div>

                            {/* Scheduled Time */}
                            <div className="pt-0.5">
                              <input
                                type="datetime-local"
                                value={item.scheduledTime}
                                onChange={(e) => handleBulkQueueUpdate(item.id, "scheduledTime", e.target.value)}
                                min={new Date().toISOString().slice(0, 16)}
                                className={`w-full text-xs border rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-400 transition-colors ${
                                  item.scheduledTime
                                    ? "border-green-300 bg-green-50 text-green-800"
                                    : "border-gray-200 text-gray-500"
                                }`}
                              />
                              {item.scheduledTime && (
                                <p className="text-[10px] text-green-600 mt-1 flex items-center gap-0.5">
                                  <Check className="w-2.5 h-2.5" /> Scheduled
                                </p>
                              )}
                            </div>

                            {/* Remove */}
                            <div className="flex items-start pt-1">
                              <button
                                onClick={() => handleBulkRemoveRow(item.id)}
                                className="p-1.5 rounded-lg hover:bg-red-50 text-gray-300 hover:text-red-500 transition-colors"
                                title="Remove from queue"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* ── Footer: Page Selector + Actions ── */}
              <div className="flex-none max-h-[44vh] md:max-h-none overflow-y-auto md:overflow-visible border-t border-gray-200 bg-white px-3 py-3 md:px-6 md:py-4">
                {/* Page selector */}
                <div className="mb-4">
                  <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2.5">
                    Select Facebook Pages
                  </p>
                  {pages.length === 0 ? (
                    <div className="rounded-xl border-2 border-dashed border-amber-300 bg-amber-50 p-3 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                      <p className="text-sm text-amber-800">
                        No Facebook pages connected — go to <strong>Facebook Page Stats</strong> to connect.
                      </p>
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {pages.map((page) => {
                        const pictureUrl = getFacebookPagePictureUrl(page.page_id) || page.page_picture_url;
                        const pictureFailed = pageImgErrors[page.page_id];
                        return (
                          <button
                            key={page.page_id}
                            onClick={() => toggleBulkPage(page.page_id)}
                            className={`max-w-full flex items-center gap-2 px-3 py-2 rounded-xl border-2 transition-all text-sm ${
                              bulkPageIds.includes(page.page_id)
                                ? "border-indigo-500 bg-indigo-50 text-indigo-800 shadow-sm"
                                : "border-gray-200 text-gray-600 hover:border-gray-300 bg-white"
                            }`}
                          >
                            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center text-indigo-600 font-medium text-xs flex-shrink-0 overflow-hidden">
                              {pictureUrl && !pictureFailed ? (
                                <img
                                  src={pictureUrl}
                                  alt=""
                                  className="w-full h-full object-cover rounded-full"
                                  onError={() =>
                                    setPageImgErrors((prev) => ({ ...prev, [page.page_id]: true }))
                                  }
                                />
                              ) : (
                                page.page_name?.charAt(0)
                              )}
                            </div>
                            <span className="font-medium truncate">{page.page_name}</span>
                            {bulkPageIds.includes(page.page_id) && (
                              <Check className="w-3.5 h-3.5 text-indigo-500" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Summary + Submit */}
                <div className="flex items-stretch md:items-center justify-between gap-4 flex-wrap">
                  <div className="w-full md:w-auto text-sm text-gray-500 flex flex-wrap items-center gap-3">
                    {bulkQueue.length > 0 ? (
                      <>
                        <span>
                          <span className="font-semibold text-gray-800">{bulkQueue.length}</span> post{bulkQueue.length !== 1 ? "s" : ""} in queue
                        </span>
                        {bulkQueue.filter((i) => i.scheduledTime).length > 0 && (
                          <span className="text-green-600 flex items-center gap-1">
                            <Check className="w-3.5 h-3.5" />
                            {bulkQueue.filter((i) => i.scheduledTime).length} scheduled
                          </span>
                        )}
                        {bulkPageIds.length > 0 && (
                          <span className="text-indigo-600">
                            → {bulkPageIds.length} page{bulkPageIds.length !== 1 ? "s" : ""}
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="text-gray-400 text-xs italic">No articles selected</span>
                    )}
                  </div>

                  <div className="w-full md:w-auto flex flex-wrap items-center gap-2 md:gap-3">
                    {bulkSubmitting && (
                      <span className="text-sm text-indigo-600 flex items-center gap-1.5">
                        <div className="w-3.5 h-3.5 border-2 border-indigo-300 border-t-indigo-600 rounded-full animate-spin" />
                        Submitting batch…
                      </span>
                    )}
                    <button
                      onClick={closeModal}
                      disabled={bulkSubmitting || bulkPublishing}
                      className="flex-1 md:flex-none justify-center px-5 py-2.5 rounded-lg border border-gray-200 text-gray-700 font-medium text-sm hover:bg-gray-50 transition-colors disabled:opacity-40"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleBulkPublishNow}
                      disabled={bulkSubmitting || bulkPublishing || bulkQueue.length === 0 || bulkPageIds.length === 0 || bulkQueue.every((item) => bulkPageIds.every((pageId) => item.publishedPageIds?.includes(pageId)))}
                      className="flex-1 md:flex-none justify-center inline-flex items-center gap-2 px-4 md:px-6 py-2.5 rounded-lg bg-blue-600 text-white font-medium text-sm hover:bg-blue-700 transition-colors shadow-md disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      {bulkPublishing ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Publishing…
                        </>
                      ) : (
                        <>
                          <Globe className="w-4 h-4" />
                          Publish Now
                        </>
                      )}
                    </button>
                    <button
                      onClick={handleBulkSubmit}
                      disabled={bulkSubmitting || bulkPublishing || bulkQueue.length === 0 || bulkPageIds.length === 0}
                      className="flex-1 md:flex-none justify-center inline-flex items-center gap-2 px-4 md:px-6 py-2.5 rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-medium text-sm hover:from-indigo-700 hover:to-purple-700 transition-all shadow-md disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      {bulkSubmitting ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Scheduling…
                        </>
                      ) : (
                        <>
                          <Calendar className="w-4 h-4" />
                          Schedule {bulkQueue.length > 0 ? `${bulkQueue.length} Post${bulkQueue.length !== 1 ? "s" : ""}` : "Posts"}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>

            </div>
          </div>
        )}
      {/* Edit Post Modal */}
      {showEditModal && editingPost && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => { setShowEditModal(false); setEditingPost(null); }}
          />
          <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl animate-fade-in">
            <div className="border-b border-gray-100 px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <h2 className="text-xl font-semibold text-gray-900">Edit Scheduled Post</h2>
              <button
                onClick={() => { setShowEditModal(false); setEditingPost(null); }}
                className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Post Content</label>
                <textarea
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  rows={5}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">First Comment</label>
                <textarea
                  value={editFirstComment}
                  onChange={(e) => setEditFirstComment(e.target.value)}
                  placeholder="Article link or additional comment..."
                  rows={2}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Scheduled Time
                </label>
                <input
                  type="datetime-local"
                  value={editScheduledAt}
                  onChange={(e) => setEditScheduledAt(e.target.value)}
                  min={new Date().toISOString().slice(0, 16)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="border-t border-gray-100 px-6 py-4 flex items-center justify-end gap-3 rounded-b-2xl">
              <button
                onClick={() => { setShowEditModal(false); setEditingPost(null); }}
                className="px-5 py-2.5 rounded-lg border border-gray-200 text-gray-700 font-medium text-sm hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={editSaving}
                className="px-6 py-2.5 rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-medium text-sm hover:from-indigo-700 hover:to-purple-700 transition-all shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {editSaving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save Changes"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </SidebarProvider>
  );
}
